import React, { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import ErrorMessage from '../../components/errormessage';
import ThemedSpinner from '../../components/themed/ThemedSpinner';
import { enforceSlash } from '../../utils/helpers';
import { useStores } from '../../hooks/use-stores';
import { observer } from 'mobx-react';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import DoneIcon from '@material-ui/icons/Done';
import ClearIcon from '@material-ui/icons/Clear';
import SimpleTable from '../../components/super/SimpleTable';
import { Fab, Tooltip } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import { makeStyles } from '@material-ui/core/styles';
import NumberInput from '../../components/input/NumberInput';
import Button from '@material-ui/core/Button';
import { runInAction } from 'mobx';

const useStyles = makeStyles((theme) => ({
    fab: {
        position: 'fixed',
        bottom: theme.spacing(4),
        right: theme.spacing(4),
    },
}));

function AssessmentTemplates() {
    const { superStore } = useStores();
    const classes = useStyles();
    const {
        loadingAssessmentTemplates,
        assessmentTemplates,
        sortedAssessmentTemplates,
        assessmentTemplatesError,
    } = superStore;
    const { pathname } = useLocation();
    const baseUrl = enforceSlash(pathname);
    const navigate = useNavigate();

    const loadData = async () => {
        superStore.loadAssessmentTemplates();
    };

    const reorderAssessmentTemplates = () => {
        runInAction(() => {
            superStore.reorderingAssessmentTemplates = true;
            superStore.assessmentTemplates = superStore.sortedAssessmentTemplates;
        });

        setTimeout(() => {
            runInAction(() => {
                superStore.reorderingAssessmentTemplates = false;
            });
        }, 0);
    };

    const updateOrder = async (templateId, orderNumber) => {
        superStore.updateAssessmentTemplateOrder(templateId, orderNumber);
        await superStore.debouncedSaveAssessmentTemplateOrder(
            templateId,
            orderNumber
        );
    };

    useEffect(() => {
        loadData();
    }, []);

    const columns = React.useMemo(
        () => [
            {
                Header: 'Id',
                accessor: 'id',
                Cell: (x) => (
                    <Link to={`${baseUrl}${x.value}`}>
                        {x.value}
                    </Link>
                ),
            },
            {
                Header: 'Title',
                accessor: 'title',
                Cell: (x) => (
                    <Link to={`${baseUrl}${x.row.original.id}`}>
                        {x.value}
                    </Link>
                ),
            },
            {
                Header: '# of Questions',
                accessor: 'questions',
                Cell: (x) => {
                    const questions = x.row.original.questions;
                    return questions ? questions.length : 0;
                },
            },
            {
                Header: 'Order',
                accessor: 'order_number',
                textAlign: 'center',
                Cell: observer((x) => {
                    let value = superStore.assessmentTemplates.find(
                        (template) => template.id === x.row.original.id
                    )?.order_number || 0;

                    return (
                        <NumberInput
                            value={value}
                            onChange={(orderNumber) =>
                                updateOrder(x.row.original.id, orderNumber)
                            }
                            min={0}
                            step={1}
                        />
                    );
                }),
            },
        ],
        [baseUrl]
    );

    return (
        <Grid
            container
            direction="row"
            justify="center"
            align="center"
            spacing={2}
        >
            <Tooltip title="Create assessment template" aria-label="add" placement="left">
                <Fab
                    color="primary"
                    onClick={() => navigate('/content/assessment-templates/create')}
                    className={classes.fab}
                >
                    <AddIcon />
                </Fab>
            </Tooltip>
            <Grid item xs={12}>
                <Box mt={4}>
                    <Typography variant="h2">Assessment Templates</Typography>
                </Box>
            </Grid>
            {loadingAssessmentTemplates ? (
                <ThemedSpinner />
            ) : assessmentTemplates && assessmentTemplates.length > 0 ? (
                <Grid item xs={12}>
                    <SimpleTable
                        columns={columns}
                        data={sortedAssessmentTemplates}
                        needsFilter={true}
                        extraAction={
                            <Button
                                onClick={reorderAssessmentTemplates}
                                color="primary"
                                variant="contained"
                            >
                                Reorder
                            </Button>
                        }
                    />
                </Grid>
            ) : (
                <Grid item xs={12}>
                    <Typography>No assessment templates found :(</Typography>
                </Grid>
            )}
            {assessmentTemplatesError && (
                <Grid item>
                    <ErrorMessage error={assessmentTemplatesError} />
                </Grid>
            )}
        </Grid>
    );
}

export default observer(AssessmentTemplates);

